// @erp/module-audit/fe · RecordHistory — the per-record trail panel.
//
// WHAT IT IS: a who/what/when timeline of everything that happened TO one
// record, with an expandable field diff, keyset "load more" paging and an
// honest empty state. It reads the record view (audit_event_subject_at_idx) —
// GET /api/audit/trail/record/:entity/:entityId.
//
// WHERE IT GOES: another workflow MOUNTS this on the detail screens (invoice,
// loan, member, announcement…). This lane only builds and exports it, so the
// component owns no route, no layout chrome above a Card, and no assumptions
// about its host beyond a QueryClientProvider.
//
// HONESTY, deliberately visible in the UI:
//   • The empty state says reads are not recorded — otherwise "no activity" on a
//     record someone demonstrably opened reads as a broken audit trail.
//   • An unresolvable actor renders "Deleted user" with the raw id, never a
//     blank: the trail outliving its actor is normal, not a rendering failure.
//   • A redacted field says "hidden" rather than showing an empty value — the
//     reader must be able to tell "changed to nothing" from "we will not say".
import { createEffect, createMemo, createSignal, For, on, Show, type JSX } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { queryState } from "@erp/ui/route-kit";
import {
  ChevronDown,
  ChevronRight,
  History,
} from "lucide-solid";
import { Button } from "@erp/ui/button";
import { Card, CardHeader } from "@erp/ui/card";
import { EmptyState } from "@erp/ui/empty-state";
import { Skeleton, TextSkeleton } from "@erp/ui/skeleton";
import { StatusBadge } from "@erp/ui/status-badge";
import type { Tone } from "@erp/ui/status-badge";
import type { AuditEventDto, AuditFieldChange, AuditSeverity } from "../schemas/index.js";
import { auditQueries } from "./queries.js";

export interface RecordHistoryProps {
  /** The entity TYPE as the trail stores it — the singular route noun ('invoice', 'loan', 'member'). */
  entity: string;
  /** The record's opaque id. */
  entityId: string;
  /** Denser type and no Card chrome — for a sidebar or a tab body that already has a surface. */
  compact?: boolean;
  /** Rows per page (keyset). Defaults to 20. */
  pageSize?: number;
  class?: string;
}

const SEVERITY_TONE: Record<AuditSeverity, Tone> = {
  info: "neutral",
  warning: "warning",
  alert: "error",
};

/** Every class string below is a COMPLETE static literal — nothing assembles a class name at runtime. */
const RAIL_DOT: Record<AuditSeverity, string> = {
  info: "bg-base-300",
  warning: "bg-warning",
  alert: "bg-error",
};

const stamp = (at: number): string =>
  new Date(at).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** '<module>.<entity>.<verb>' → 'Verb entity' — the action string is the source of truth, this only reads it out. */
const actionLabel = (action: string): string => {
  const parts = action.split(".");
  const verb = parts.at(-1) ?? action;
  const noun = parts.length >= 2 ? parts.at(-2) : undefined;
  const pretty = `${verb} ${noun ?? ""}`.trim().replace(/[-_]/g, " ");
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

const actorLabel = (row: AuditEventDto): string => {
  if (row.actorKind === "anon") return "Unauthenticated";
  if (row.actorKind === "system") return "System";
  if (row.actorName) return row.actorName;
  // The trail outlived the account. Say so, and keep the id — it is still the join key.
  return row.actor ? `Deleted user (${row.actor})` : "Unknown";
};

const changeValue = (v: AuditFieldChange["before"]): string =>
  v === null ? "—" : typeof v === "string" ? (v.length === 0 ? "(empty)" : v) : String(v);

function FieldDiff(props: { change: AuditFieldChange }): JSX.Element {
  return (
    <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-baseline gap-x-3 gap-y-1 py-1 sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)]">
      <dt class="truncate text-xs font-medium text-base-subtle">{props.change.field}</dt>
      <dd class="min-w-0 text-xs text-base-muted">
        <Show
          when={!props.change.redacted}
          fallback={<span class="italic text-base-subtle">changed — value hidden</span>}
        >
          <span class="break-words text-base-subtle line-through">{changeValue(props.change.before)}</span>
          <span class="px-1.5 text-base-subtle" aria-hidden="true">
            →
          </span>
          <span class="break-words font-medium">{changeValue(props.change.after)}</span>
        </Show>
      </dd>
    </div>
  );
}

function HistorySkeleton(props: { rows: number }): JSX.Element {
  return (
    <div aria-hidden="true" data-skeleton="record-history" class="flex flex-col gap-4">
      <For each={Array.from({ length: props.rows }, (_, i) => i)}>
        {() => (
          <div class="flex gap-3">
            <Skeleton shape="pill" class="mt-1 h-2.5 w-2.5 shrink-0" />
            <div class="min-w-0 flex-1">
              <Skeleton class="h-4 w-44" />
              <TextSkeleton lines={1} class="mt-2" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

export function RecordHistory(props: RecordHistoryProps): JSX.Element {
  const pageSize = () => props.pageSize ?? 20;
  const [cursor, setCursor] = createSignal<string | undefined>(undefined);
  const [rows, setRows] = createSignal<AuditEventDto[]>([]);
  const [open, setOpen] = createSignal<ReadonlySet<string>>(new Set<string>());

  // queryState: `trail.data()` never suspends, whoever reads it. This panel was
  // already safe by construction — the accumulate effect is a user effect (no
  // resource tracking) and the body memo returns the bones before any JSX
  // reads `nextCursor()` — so the +3 s "audit-history" wait the audit measured
  // was the HOST detail screen suspending, not this panel. The helper makes
  // that safety a property of the reads rather than of their order: a reader
  // added outside the first-load gate cannot detach the host it is mounted in.
  const trail = queryState(useQuery(() =>
    auditQueries.recordTrail(props.entity, props.entityId, { limit: pageSize(), cursor: cursor() }),
  ));

  // A new record ⇒ a new timeline. Reset BEFORE the next page lands so the
  // accumulator never shows one record's history under another's heading.
  createEffect(
    on(
      () => [props.entity, props.entityId] as const,
      () => {
        setRows([]);
        setOpen(new Set<string>());
        setCursor(undefined);
      },
      { defer: true },
    ),
  );

  // Accumulate pages. Deduped by id (ULIDs, globally unique) because
  // keepPreviousData briefly re-serves the previous page while the next loads —
  // without the guard, "load more" would double every visible row.
  createEffect(() => {
    const page = trail.data();
    if (!page) return;
    setRows((prev) => {
      const seen = new Set(prev.map((r) => r.id));
      const fresh = page.data.filter((r) => !seen.has(r.id));
      return fresh.length > 0 ? [...prev, ...fresh] : prev;
    });
  });

  const toggle = (id: string): void => {
    setOpen((prev) => {
      const next = new Set<string>(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const nextCursor = () => trail.data()?.nextCursor ?? null;
  const isFirstLoad = () => trail.pending() && rows().length === 0;
  const changesOf = (row: AuditEventDto): AuditFieldChange[] =>
    (row.detail?.diffs ?? []).flatMap((d) => d.changed);

  const body = createMemo((): JSX.Element => {
    if (isFirstLoad()) return <HistorySkeleton rows={props.compact ? 3 : 5} />;
    const err = trail.error();
    if (err !== null)
      return (
        <EmptyState
          compact={props.compact}
          icon={<History size={props.compact ? 18 : 20} />}
          title="History unavailable"
          description={err instanceof Error ? err.message : "The audit trail could not be read."}
        />
      );
    if (rows().length === 0)
      return (
        <EmptyState
          compact={props.compact}
          icon={<History size={props.compact ? 18 : 20} />}
          title="No recorded activity"
          description="Every create, update and delete on this record appears here. Reads are not recorded."
        />
      );
    return (
      <>
        <ol class="flex flex-col">
          <For each={rows()}>
            {(row) => {
              const changes = () => changesOf(row);
              const expanded = () => open().has(row.id);
              return (
                <li class="relative flex gap-3 pb-4 last:pb-0">
                  {/* The rail: a dot per event, a hairline connecting them. */}
                  <div class="relative flex w-2.5 shrink-0 justify-center">
                    <span
                      class={`mt-1.5 size-2.5 shrink-0 rounded-full ${RAIL_DOT[row.severity]}`}
                      aria-hidden="true"
                    />
                    <span
                      class="absolute top-5 bottom-0 w-px bg-base-300 last:hidden"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span class={props.compact ? "text-xs font-bold" : "text-sm font-bold"}>
                        {actionLabel(row.action)}
                      </span>
                      <Show when={row.severity !== "info"}>
                        <StatusBadge label={row.severity} tone={SEVERITY_TONE[row.severity]} />
                      </Show>
                      <Show when={row.status !== null && row.status >= 400}>
                        <span class="text-xs font-medium tabular-nums text-error">
                          refused · {row.status}
                        </span>
                      </Show>
                    </div>
                    <p class="mt-0.5 text-xs text-base-subtle">
                      {actorLabel(row)}
                      <span class="px-1.5 text-base-subtle" aria-hidden="true">
                        ·
                      </span>
                      <time datetime={new Date(row.at).toISOString()}>{stamp(row.at)}</time>
                    </p>
                    <Show when={changes().length > 0}>
                      <button
                        type="button"
                        class="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-base-subtle hover:text-base-content"
                        aria-expanded={expanded()}
                        onClick={() => toggle(row.id)}
                      >
                        <Show when={expanded()} fallback={<ChevronRight size={13} aria-hidden="true" />}>
                          <ChevronDown size={13} aria-hidden="true" />
                        </Show>
                        {changes().length} field{changes().length === 1 ? "" : "s"} changed
                      </button>
                      <Show when={expanded()}>
                        <dl class="mt-1.5 rounded-500 border border-base-300 bg-base-200 px-3 py-2">
                          <For each={changes()}>{(change) => <FieldDiff change={change} />}</For>
                          <Show when={row.detail?.truncated}>
                            <p class="pt-1 text-xs italic text-base-subtle">
                              Some changes were omitted — the diff exceeded its size cap.
                            </p>
                          </Show>
                        </dl>
                      </Show>
                    </Show>
                  </div>
                </li>
              );
            }}
          </For>
        </ol>
        <Show when={nextCursor()}>
          {(next) => (
            <div class="pt-1">
              <Button
                size="sm"
                variant="ghost"
                loading={trail.refreshing()}
                onClick={() => setCursor(next())}
              >
                Load older
              </Button>
            </div>
          )}
        </Show>
      </>
    );
  });

  return (
    <Show
      when={!props.compact}
      fallback={
        <section class={`flex flex-col gap-3 ${props.class ?? ""}`} aria-label="Record history">
          {body()}
        </section>
      }
    >
      <Card class={props.class}>
        <CardHeader icon={History} title="History" subtitle="Who changed what, and when" />
        <div class="p-5">{body()}</div>
      </Card>
    </Show>
  );
}
